import Yup from '~/util/yup';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { FormModal } from '~/shared/forms/FormModal';
import {
	getPatientDemographicsOptions,
	editPatientDemographicsOptions,
	LanguageOptions,
	NeedsInterpreterOptions,
	RaceOptions,
	EthnicityOptions,
	MaritalStatusOptions,
	ReligionOptions,
	EducationLevelOptions
} from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { CheckboxGroupFormField } from '~/shared/forms/controls/fields/CheckboxGroupFormField';

function getEnglishAtTop(): {
	id: number;
	label: string;
}[] {
	const english = LanguageOptions.find(x => x.label === 'English');
	if (english) {
		return [english, ...LanguageOptions.filter(x => x.label !== 'English')];
	}
	return LanguageOptions;
}

export function PatientDemographicsFormModal({
	stayId,
	validationSchema,
	closeModal
}: {
	stayId: string;
	validationSchema: Yup.AnySchema;
	closeModal: () => void;
}) {
	const { data: demographics, isLoading } = useSuspenseQuery(getPatientDemographicsOptions(stayId));
	const englishAtTop = getEnglishAtTop();
	if (isLoading || !demographics) {
		return null;
	}

	const initialValues = {
		alternatePhoneNumber: null,
		emailAddress: null,
		maritalStatusId: null,
		educationLevelId: null,
		primaryLanguageId: null,
		religionId: null,
		needsInterpreterId: null,
		...demographics
	};

	return (
		<FormModal
			title="Edit Patient Demographics"
			initialValues={initialValues}
			saveMutation={{
				...editPatientDemographicsOptions,
				invalidateQueryKeys: [getPatientDemographicsOptions(stayId).queryKey]
			}}
			validationSchema={validationSchema}
			onSuccess={closeModal}
			closeModal={closeModal}
		>
			<div className="grid-cols-1 space-y-6">
				<div className="mb-4 space-y-3">
					<div className="grid grid-cols-3 gap-4">
						<PhoneNumberFormField name="mainPhoneNumber" />
						<PhoneNumberFormField name="alternatePhoneNumber" />
						<TextFormField name="emailAddress" />
					</div>
					<div className="grid grid-cols-2 gap-4">
						<SelectFormField name="primaryLanguageId" options={englishAtTop} />
						<SelectFormField name="needsInterpreterId" options={NeedsInterpreterOptions} />
					</div>
				</div>
				<div className="mb-4 space-y-3">
					<div className="grid grid-cols-1 gap-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="col-span-1">
								<CheckboxGroupFormField
									name="raceIds"
									description="(Check all that apply.)"
									options={RaceOptions}
									containerClassName="grid grid-cols-1 gap-4"
								/>
							</div>
							<div className="col-span-1">
								<CheckboxGroupFormField
									name="ethnicityIds"
									description="Is the patient of Hispanic, Latino/a, or Spanish origin? (Check all that apply.)"
									options={EthnicityOptions}
									containerClassName="grid grid-cols-1 gap-4"
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="mb-4 space-y-3">
					<div className="grid grid-cols-2 gap-4">
						<SelectFormField name="maritalStatusId" options={MaritalStatusOptions} />
						<SelectFormField name="religionId" options={ReligionOptions} />
						<SelectFormField name="educationLevelId" options={EducationLevelOptions} />
					</div>
				</div>
			</div>
		</FormModal>
	);
}
